import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faClipboardList } from '@fortawesome/pro-regular-svg-icons/faClipboardList';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Space,
  Switch,
  type TableColumnsType,
  theme,
  Tooltip,
} from 'antd';
import { useCreateEditAssetQuery } from 'pos-client/components/assets/hooks/useCreateEditAssetQuery.tsx';
import {
  AssetModal,
  type AssetModalRefProps,
} from 'pos-client/components/assets/Modals/AssetModal.tsx';
import { TypographyShowHide } from 'pos-client/components/generic/typography-show-hide/TypographyShowHide.tsx';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import {
  PosDisabledWarning,
  type PosDisabledWarningType,
} from 'pos-client/components/shared/General/PosDisabledWarning.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useLocationAssignedAssets } from 'pos-client/hooks/useLocationAssignedAssets.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import type { ModeType } from 'pos-client/pages/locations/Modals/useModalType.tsx';
import { GREEN, LIGHT_GREY } from 'pos-client/utils/colours.ts';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type {
  AssetModel,
  CreateAssetModel,
  UpdateAssetModel,
} from 'pos-shared/models/assetSchema.ts';
import type { IdModel } from 'pos-shared/models/commonSchema.ts';
import type {
  LocationLinkedAssetModel,
  LocationModel,
} from 'pos-shared/models/locationSchema.ts';

import { AssetCapabilitiesList } from './AssetCapabilitiesList.tsx';

export const LocationAssets = ({
  isLocationOverview,
}: {
  isLocationOverview?: boolean;
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const canAddAndEditAssets = hasPermissions('add.edit.location.assets');
  const ref = useRef<AssetModalRefProps>(null);

  const trpcContext = trpc.useUtils();

  const { location } = useOutletContext<{
    location: LocationModel;
  }>();

  const {
    createAsset,
    updateAsset,
    deleteAsset,
    assignAsset,
    isLoading: isCreatingOrUpdating,
  } = useCreateEditAssetQuery();
  const { filteredAssets, isLoading } = useLocationAssignedAssets(location.id);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCapabilities, setShowCapabilities] = useState<boolean>(false);
  const [modalData, setModalData] = useState<AssetModel | undefined>();
  const { token } = theme.useToken();

  const maxActiveRampsReached = useMemo(() => {
    const activeRamps =
      filteredAssets?.filter(
        (asset) =>
          asset.assetTypeName?.toLowerCase() === 'ramp' && asset.isActive
      ) ?? [];

    return activeRamps.length >= 20;
  }, [filteredAssets]);

  const onlyMobileServiceType = useMemo(() => {
    return location.serviceTypes?.every(
      (serviceType) => serviceType === 'Mobile'
    );
  }, [location?.serviceTypes]);

  const showEditModal = (record: AssetModel & { key?: string }) => {
    setModalData(record);
    setIsModalOpen(true);
  };
  const showCreateModal = () => {
    setModalData(undefined);
    setIsModalOpen(true);
  };
  const handleAssetModalCancel = () => {
    setModalData(undefined);
    setShowCapabilities(false);
    setIsModalOpen(false);
  };

  const handleAssetModalOk = async (
    data: CreateAssetModel | UpdateAssetModel | IdModel,
    mode: ModeType
  ) => {
    switch (mode) {
      case 'edit':
        updateAsset(data as UpdateAssetModel, {
          async onSuccess() {
            await finishAction();
          },
        });
        break;
      case 'create':
        createAsset(data as CreateAssetModel, {
          async onSuccess(data: IdModel) {
            await assignAsset(
              { assetId: data.id, id: location.id },
              {
                onSuccess() {
                  finishAction();
                },
              }
            );
          },
        });
        break;
      case 'delete':
        deleteAsset(data as IdModel, {
          async onSuccess() {
            await finishAction();
          },
        });
        break;
    }
  };

  const finishAction = async () => {
    await trpcContext.locations.getLocationLinkedAssets.invalidate({
      id: location.id,
    });
    setShowCapabilities(false);
    handleAssetModalCancel();
    ref?.current?.resetForm();
  };

  const { showModal: showRemoveModal } = useConfirmModal({
    type: 'delete',
    content: t('assets.deleteAssetConfirm'),
    confirmText: t('assets.deleteAsset'),
    confirmProps: {
      ghost: true,
    },
  });

  const handleRemoveAsset = (asset: AssetModel) => {
    const assetId = asset.id;

    if (assetId) {
      showRemoveModal({
        title: `${t('assets.deleteAsset')} ${asset.name}`,
        onConfirm: () => {
          handleAssetModalOk({ id: assetId }, 'delete');
          ref?.current?.resetForm();
        },
      });
    }
  };

  const handleAssetStatusUpdate = (asset: AssetModel) => {
    const assetId = asset.id;

    if (assetId) {
      updateAsset(asset as UpdateAssetModel, {
        async onSuccess() {
          await finishAction();
        },
      });
    }
  };

  const addActivateRampDisabledWarnings = (
    asset: LocationLinkedAssetModel
  ): PosDisabledWarningType => {
    return {
      condition:
        asset.assetTypeName?.toLowerCase() === 'ramp' &&
        !asset.isActive &&
        maxActiveRampsReached &&
        !onlyMobileServiceType,
      key: 'maxRamps',
      text: t('assets.max20Ramps'),
    };
  };

  const columns: TableColumnsType<LocationLinkedAssetModel> = [
    {
      title: t('assets.assetType'),
      key: 'assetTypeName',
      dataIndex: 'assetTypeName',
    },
    {
      title: t('assets.assetName'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('assets.assetReference'),
      key: 'reference',
      dataIndex: 'reference',
      render: (value) => {
        if (!value) {
          return (
            <Tooltip title={t('assets.noReferenceFound')}>
              <span>-</span>
            </Tooltip>
          );
        } else {
          return value;
        }
      },
    },
    {
      title: t('assets.assetDescription'),
      key: 'description',
      dataIndex: 'description',
      render: (content) => {
        if (!content) {
          return <span>-</span>;
        } else {
          return <TypographyShowHide content={content} />;
        }
      },
    },
    {
      title: t('assets.assetCapabilities'),
      key: 'skills',
      dataIndex: 'skills',
      width: 200,
      render: (skills) => {
        return <AssetCapabilitiesList skills={skills} />;
      },
    },
    {
      title: t('assets.assetsStatus'),
      key: 'isActive',
      dataIndex: 'isActive',
      width: 120,
      render: (_, record) => {
        const disabledWarningError = addActivateRampDisabledWarnings(record);

        return !canAddAndEditAssets ? (
          <Space
            size={0}
            style={{
              backgroundColor: record.isActive ? GREEN.color : LIGHT_GREY,
              color: token.colorWhite,
              borderRadius: '50px',
              paddingLeft: token.paddingXS,
              paddingRight: token.paddingXS,
              fontSize: token.fontSizeSM,
              height: '22px',
            }}
          >
            {record.isActive === true ? (
              <>
                <FontAwesomeIcon
                  icon={faCheck}
                  color={token.colorWhite}
                  style={{
                    marginRight: token.marginXXS,
                  }}
                />
                {t('statuses.active')}
              </>
            ) : (
              t('statuses.inactive')
            )}
          </Space>
        ) : (
          <PosDisabledWarning warnings={disabledWarningError}>
            <Switch
              style={{
                backgroundColor: record.isActive ? GREEN.color : LIGHT_GREY,
              }}
              checkedChildren={
                <>
                  <FontAwesomeIcon
                    icon={faCheck}
                    color={token.colorWhite}
                    style={{
                      marginRight: token.marginXXS,
                    }}
                  />
                  {t('statuses.active')}
                </>
              }
              checked={record.isActive === true}
              unCheckedChildren={t('statuses.inactive')}
              aria-label={t('common.status.status')}
              defaultChecked={record.isActive === true}
              disabled={isCreatingOrUpdating || disabledWarningError.condition}
              onChange={() =>
                handleAssetStatusUpdate({
                  ...record,
                  isActive: record.isActive === true ? false : true,
                })
              }
            />
          </PosDisabledWarning>
        );
      },
    },
    {
      title: t('common.audit.dateModified'),
      key: 'lastModified',
      dataIndex: 'lastModified',
      render: (value) => convertISOStringToLocalDate(value),
    },
    {
      title: '',
      key: 'id',
      dataIndex: 'id',
      width: 100,
      align: 'center',
      render: (_, record) =>
        record?.assetTypeName?.toLowerCase() !== 'technician' && (
          <Space>
            <Button size="small" onClick={() => showEditModal(record)}>
              {t('common.buttons.edit')}
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleRemoveAsset(record)}
            >
              {t('common.buttons.delete')}
            </Button>
          </Space>
        ),
      hidden: !canAddAndEditAssets,
    },
  ];

  const tableData = useTableData(
    filteredAssets?.filter(
      (x) => x?.assetTypeName?.toLowerCase() !== 'technician'
    ),
    (x) => String(x.id)
  );

  useEffect(() => {
    if (!isModalOpen) {
      setModalData(undefined);
    }
  }, [isModalOpen]);

  const actionComponent = () => {
    if (isLocationOverview) {
      return (
        <Button>
          <Link to={`/locations/${location.id}/assets`}>
            {t('assets.manageAssets')}
          </Link>
        </Button>
      );
    }
    if (canAddAndEditAssets) {
      return (
        <Button
          onClick={showCreateModal}
          icon={<FontAwesomeIcon icon={faPlus} />}
        >
          {t('common.buttons.addNew')}
        </Button>
      );
    }
  };

  return (
    <>
      <PosCard
        headerProps={{
          title: t('assets.assets'),
          icon: faClipboardList,
          action: actionComponent(),
        }}
        colProps={{
          span: 24,
        }}
      >
        <PosTable
          id="location-assets-table"
          dataSource={tableData}
          columns={columns}
          isLoading={isLoading}
          fePagination
        />
      </PosCard>
      <AssetModal
        isVisible={isModalOpen}
        data={modalData}
        handleOk={handleAssetModalOk}
        handleCancel={handleAssetModalCancel}
        showCapabilities={showCapabilities}
        setShowCapabilities={setShowCapabilities}
        ref={ref}
        isCreatingOrUpdating={isCreatingOrUpdating}
        maxActiveRampsReached={maxActiveRampsReached}
        onlyMobileServiceType={onlyMobileServiceType}
      />
    </>
  );
};
